import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
  enableBodyScroll,
  ifDefined,
  disableBodyScroll,
} from '@hh/global.js'

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    labelledby: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    _height: {
      state: true,
    },
    linkUrl: {
      type: String,
      attribute: 'link-url',
    },
    linkText: {
      type: String,
      attribute: 'link-text',
    },
    linkId: {
      type: String,
      attribute: 'link-id',
    },
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.heightOffset = 3
    this.breakpoint = '(min-width: 990px)'

    // Bind methods once
    this.bodyListener = this.bodyListener.bind(this)
    this.closeWithEscape = this.closeWithEscape.bind(this)
    this.close = this.close.bind(this)

    this.time = 0
    this.timeOut = 0
    this.linkUrl = '#'
    this.linkText = ''
    this.linkId = ''
  }

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener('keyup', this.closeWithEscape, { passive: true })
    document.body.addEventListener('closeModal', this.close)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('keyup', this.closeWithEscape)
    document.body.removeEventListener('closeModal', this.close)
    this.removeBodyListener()
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  openMegamenu(e) {
    this.querySelector('.top-menu__menu-item').setAttribute(
      'aria-hidden',
      'false'
    )
    clearTimeout(this.timeOut)
    this.isMouseHover = true
    this.time = setTimeout(() => {
      this.toggle(e, true)
      if (this.bodyClasses !== '') {
        document.body.classList.add(this.bodyClasses)
      }
    }, 0)
  }

  toggleLeaveMegaMenu() {
    this.querySelector('.top-menu__menu-item').setAttribute(
      'aria-hidden',
      'true'
    )
    clearTimeout(this.time)
    this.addEventListener('mouseleave', () => {
      this.isMouseHover = false
      if (this.open) {
        this.timeOut = setTimeout(() => {
          this.toggle(false, false)
        }, 50)
      }
    })
  }

  toggle(e = false, value = undefined) {
    if (e.type === 'keyup' && e.code.toUpperCase() !== 'ENTER') {
      return true
    }

    e && e.preventDefault()

    this.open = typeof value === 'undefined' ? !this.open : value
    this[`${this.open ? 'add' : 'remove'}BodyListener`]()
    this.open && this.lockHeight && this.setHeight()
    this.open && this.broadcast()
    // closes the client services drawer when the modal is open
    this.open &&
      document
        .querySelector('.header-wrapper')
        ?.classList.contains('client-services--open') &&
      this.dispatchEvent(
        new CustomEvent('drawer:close', { bubbles: true, composed: true })
      )
  }

  closeFromDetails(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.toggle()
    }
  }

  closeWithEscape(e) {
    if (e.code?.toUpperCase() === 'ESCAPE' && this.open) {
      this.open = false
      this.isMouseHover = false

      const button = this.shadowRoot.querySelector('button.header__menu-item')

      if (button) {
        requestAnimationFrame(() => {
          button.focus({
            focusVisible: false,
          })
        })
      }
    }
  }

  close() {
    this.open && this.toggle()
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  addBodyListener() {
    this.isMobile() && disableBodyScroll(this.$container?.value)
    document.body.addEventListener('click', this.bodyListener)
  }

  removeBodyListener() {
    enableBodyScroll(this.$container?.value)
    document.body.removeEventListener('click', this.bodyListener)
  }

  bodyListener(e) {
    !e._intercepted && this.toggle()
  }

  setHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}

    this._height = this.isMobile()
      ? `calc(100vh - ${Math.floor(
          parentTop + detailsTop - this.heightOffset
        )}px)`
      : ''
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  get focusActive() {
    return !this.isMouseHover && this.open
  }

  render() {
    return html`
      <div
        class="menu-container"
        @mouseover="${this.openMegamenu}"
        @mouseleave="${this.toggleLeaveMegaMenu}"
        @focus="${this.openMegamenu}"
        @blur="${this.toggleLeaveMegaMenu}"
      >
        <div class="menu-container-inner">
          ${
            this.linkUrl
              ? html`<a
                  href="${this.linkUrl}"
                  class="header__menu-item header__menu-item--parent utility header-item"
                  aria-expanded="${this.open}"
                >
                  <slot
                    class="${classMap({ 'is-open': this.open })}"
                    name="summaryOnHover"
                    tabindex="-1"
                  ></slot>
                </a>`
              : html`<span
                  role="button"
                  class="header__menu-item header__menu-item--parent utility header-item"
                  aria-expanded="${this.open}"
                  aria-controls="menu-${this.linkId}"
                >
                  <slot
                    class="${classMap({ 'is-open': this.open })}"
                    name="summaryOnHover"
                    tabindex="-1"
                  ></slot>
                </span>`
          }
          <button
            class="header__menu-dropdown-trigger ${this.open ? 'is-open' : ''}"
            @click="${this.toggle}"
            @keyup="${this.toggle}"
            @mouseover="${this.toggle}"
            id="menu-trigger-${this.linkId}"
            aria-label="Open ${this.linkText} menu"
          >
          <svg aria-hidden='true' focusable='false' role='presentation' class='icon icon-caret' viewBox='0 0 10 6'>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor">
    </svg></button>
        </div>

        <div
          id="menu-${this.linkId}"
          class="header__submenu--child content"
          role="region"
          aria-labelledby="menu-trigger-${this.linkId}"
          style="${styleMap({ height: this._height })}"
          ${ref(this.$details)}
        >
          <hh-focus-trap active="${this.focusActive}">
            <slot
              name="details"
              @keyup="${this.closeWithEscape}"
              @click="${this.closeFromDetails}"
            ></slot>
          </hh-focus-trap>
        </div>
      </div>
    `
  }

  static styles = css`
    button.header__menu-item {
      cursor: pointer;
      height: 100%;
      display: flex;
      align-items: center;
      text-decoration: none;
      color: inherit;
      background: transparent;
      border: none;
      padding: 0;
      position: relative;
      font: var(--hh-utility);
      letter-spacing: var(--hh-utility-letter-spacing);
      margin: 0;
      text-transform: var(--hh-utility-text-transform);
      cursor: pointer;
      text-decoration: none;
      color: inherit;
    }
    .menu-container a,
    .menu-container span {
      color: inherit;
      text-decoration: none;
    }

    .menu-container span {
      cursor: pointer;
    }

    .menu-container-inner {
      position: relative;
      padding-right: var(--item-padding-right);
    }

    button.header__menu-item:focus {
      outline: none;
    }

    button.header__menu-item:focus-visible::after {
      content: '';
      position: absolute;
      inset: 30% 0;
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .header__utility span,
    .header__inline-menu span {
      font: var(--hh-utility);
    }

    .header__menu-dropdown-trigger {
      position: absolute;
      appearance: none;
      top: 0;
      right: 10px;
      height: 100%;
      background: transparent;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--hh-nav-item-color, inherit);
    }

    .header__menu-dropdown-trigger:focus {
      opacity: 1;
      color: var(--hh-nav-item-color);
    }

    .header__menu-dropdown-trigger svg {
      transform: rotate(0deg);
      width: 6px;
    }

    .header__menu-dropdown-trigger.is-open svg {
      transform: rotate(180deg);
    }

    .content {
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--hh-modal-background);
      height: var(--hh-modal-height);
      z-index: 1;
      display: none;
    }

    :host([is-open]) .content {
      display: block;
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }
  `
}

customElements.define('hh-details-modal', HHDetailsModal)
